import { useMemo } from 'react';
import { useFetchInfiniteES, useApiClient } from '@marageti/z4-lib';
import { SalesRecording } from '@marageti/z4-sdk/lib/travel';
import {
  FilterConstraint,
  FilterOp,
  SearchRequest,
  SearchResult,
} from '@marageti/z4-sdk/lib/entity';
import { format } from 'date-fns';

// Update this interface when new search fields are added
// Use plural for arrays and singular for single values so we can support destination and destinations, for example.
interface PossibleSearchFields {
  endDate?: string;
  fullText?: string;
  order?: string;
  partnerId?: string;
  salesRecordingType?: string[];
  sort?: string;
  startDate?: string;
  status?: string[];
  tripMatchId?: string;
}

const useSalesRecordingSearch = ({
  endDate = '',
  fullText = '',
  order = '',
  partnerId = '',
  salesRecordingType = [],
  sort = '',
  startDate = '',
  status = [],
  tripMatchId = '',
}: PossibleSearchFields) => {
  const apiClient = useApiClient();

  // On rerender, create the search request
  const getSearchRequest = (): SearchRequest => {
    const filterBy: FilterConstraint[] = [];

    if (tripMatchId) {
      filterBy.push({ field: 'tripMatchId', value: [tripMatchId] });
    }

    if (partnerId && partnerId !== 'all' && partnerId !== 'none') {
      filterBy.push({ field: 'partnerId', value: [partnerId] });
    }

    // create status filter
    if (status.length > 0) {
      filterBy.push({ field: 'status', value: status });
    }
    // handle tag filter
    if (salesRecordingType.length > 0) {
      filterBy.push({ field: 'salesRecordingType', value: salesRecordingType });
    }

    if (startDate) {
      // takes a full date string and formats it to yyyy-MM-dd
      const formattedStartDate = format(new Date(startDate), 'yyyy-MM-dd');
      filterBy.push({ field: 'createdAt', op: FilterOp.FilterOpGTE, value: [`${formattedStartDate}||/d`] });
    }

    if (endDate) {
      // takes a full date string and formats it to yyyy-MM-dd
      const formattedEndDate = format(new Date(endDate), 'yyyy-MM-dd');
      filterBy.push({ field: 'createdAt', op: FilterOp.FilterOpLTE, value: [`${formattedEndDate}||/d`] });
    }

    const doFullTextSearch = fullText.length > 2;

    let sortBy;
    if (doFullTextSearch) {
      sortBy = [{ field: '_score', order: 'desc' }, { field: 'createdAt', order: 'desc' }];
    } else if (sort && order) {
      sortBy = [{ field: sort, order }];
    }

    return {
      filterBy,
      fullText: doFullTextSearch ? fullText : undefined,
      pagination: { limit: 30, page: 1 },
      ...(sortBy && { sortBy }),
    };
  };

  // Fetch and exicute search request
  const searchRequest = getSearchRequest();

  const {
    data,
    error,
    hasMore,
    isLoading,
    loadMore,
  } = useFetchInfiniteES<SalesRecording>(apiClient.salesRecordingsClient.postSalesRecordingSearch, searchRequest, { revalidateOnFocus: false });

  const salesRecordings = useMemo(() => (
    data?.flatMap((searchResult: SearchResult<SalesRecording>) => searchResult.hits)
  ), [data]);

  return {
    salesRecordings,
    error,
    hasMore,
    isLoading,
    loadMore,
    totalHits: data?.[0]?.totalHits || 0,
  };
};

export default useSalesRecordingSearch;
